import { ToolbarOptions } from "@zebrabi/global-toolbar-old/interface/ToolbarOption";
import BaseSwitcherWithHeaderOld from "./BaseSwitcherWithHeaderOld";
import { flagHandler } from "@zebrabi/zebrabi-core";
import { getColumnRole } from "@zebrabi/data-helpers/visualHelpers";
import { fieldsListOf, sourceIndexOf, valueColumnKind, type ReaderField } from "@zebrabi/data-helpers/sourceColumns";
import { getOfficeSettings } from "@zebrabi/office-settings";
import { DATA_SOURCE, type DataSource, createDialog } from "@zebrabi/data-helpers/editData";
import { FIELDS_LIST_FLAG, MeasureRoles, CategoricalRoles, type FieldRole } from "@zebrabi/office-types";
import type { DataView, DataViewCategoryColumn, DataViewMetadataColumn } from "@zebrabi/table-data";
import type { BucketDefinition, Field, FieldsPane, FieldsPaneRolesChangeEvent, RoleChange } from "@zebrabi/fields-pane";

/** Category and Group are display only, so `maxFields` never applies; Comments holds a text column, the other value buckets numbers */
const BUCKETS: BucketDefinition[] = [
    { role: CategoricalRoles.Category, label: "Category", accepts: [], maxFields: Infinity, reorderable: false },
    { role: CategoricalRoles.Group, label: "Group", accepts: [], maxFields: Infinity, reorderable: false },
    { role: MeasureRoles.Values, label: "Actual", accepts: ["measure"], maxFields: 2, reorderable: false },
    { role: MeasureRoles.PreviousYear, label: "Previous Year", accepts: ["measure"], maxFields: 1, reorderable: false },
    { role: MeasureRoles.Plan, label: "Plan", accepts: ["measure"], maxFields: 1, reorderable: false },
    { role: MeasureRoles.Forecast, label: "Forecast", accepts: ["measure"], maxFields: 1, reorderable: false },
    { role: MeasureRoles.Comments, label: "Comments", accepts: ["text"], maxFields: 1, reorderable: false },
];

/** Every flat source Charts reads (range, grid, linked data) takes its first column as the category and the rest as value fields */
const CATEGORY_COLUMNS_COUNT = 1;

/**
 * The Comments bucket takes text only once the cells are known. A pivot table and an older document list no cells,
 * so every value column there is a number and the Comments bucket has to take one, or nothing could ever enter it.
 */
function bucketsFor(missingDataCells: boolean): BucketDefinition[] {
    return BUCKETS.map((bucket: BucketDefinition): BucketDefinition => {
        const isComments = bucket.role === MeasureRoles.Comments;
        const takesNumbers = isComments && missingDataCells;

        return takesNumbers ? { ...bucket, accepts: ["measure", "text"] } : bucket;
    });
}

/** The Data fields toolbar entry: mounts the shared pane and commits what the user does in it to the settings */
export default class FieldsChartsSwitcher extends BaseSwitcherWithHeaderOld {
    static readonly CLASS_NAME = `FieldsChartsSwitcher`;

    toolbarOptions: ToolbarOptions = {
        collapsed: true,
        elementName: "Data fields",
        icon: "fields-icon-base64",
        type: "button",
        actions: [],
    };

    /** The pane of the current menu render; refreshed after each commit so it shows the new state even if the host skips the redraw */
    private pane: FieldsPane | undefined;

    /** The flag at the last menu render; a flip takes effect when the menu opens again */
    private fieldsListFlag = false;

    public getClassName(): string {
        return FieldsChartsSwitcher.CLASS_NAME;
    }

    /** Required by the base class; this switcher declares no menu actions, so it is never called */
    buttonAction(): void {}

    /** Required by the base class; nothing in the toolbar's broadcast concerns this entry */
    update(): void {}

    /**
     * Mounts the shared pane with the current columns. Runs on every menu render, and a committed change re-renders the
     * menu, so the pane is rebuilt rather than updated in place; a flag that flips mid-session takes effect at the next render.
     * @param switcherMenuContainer the menu element the old toolbar hands every switcher; the pane goes into its `.body`
     * @param active the toolbar's active-item marker, passed through to the base class and unused here
     */
    createMenuItems(switcherMenuContainer: HTMLElement, active: string = "") {
        super.createMenuItems(switcherMenuContainer, active);
        const body = switcherMenuContainer.querySelector(".body");
        const form = document.createElement("div");
        form.classList.add("fields-settings-form-2");
        this.fieldsListFlag = flagHandler.has(FIELDS_LIST_FLAG);

        const pane = document.createElement("fields-pane");
        this.pane = pane;
        pane.buckets = bucketsFor(this.dataView()?.sourceColumns === undefined);
        pane.fields = this.buildFields();
        pane.fieldsList = this.showsFieldsList();
        // the list only shows the assignment: nothing stores a column taken out of a bucket or put into Category or Group
        pane.fieldsListReadOnly = true;
        pane.dragFeedback = this.fieldsListFlag;
        pane.dataSource = this.currentDataSource();
        pane.addEventListener("fields-pane-roles-change", (event: FieldsPaneRolesChangeEvent) =>
            this.applyRoleChanges(event.detail.changes)
        );
        pane.addEventListener("fields-pane-edit-data-source", () => this.editDataSource());

        form.appendChild(pane);
        body?.appendChild(form);
    }

    /** The list needs the source's columns, which a pivot table and a document stored before they were parsed do not have */
    private showsFieldsList(): boolean {
        return this.fieldsListFlag && this.dataView()?.sourceColumns !== undefined;
    }

    /**
     * The category and group names the reader provides, then the measure columns as pane fields. With the Fields list
     * on, the same fields laid over the source columns, so the list also shows the columns the reader skipped.
     */
    private buildFields(): Field[] {
        const sourceColumns = this.dataView()?.sourceColumns;

        // a range or grid names its category through the first source column; a pivot's row and column fields stand for none
        const categories = this.categoryFields().map((field: Field, ordinal: number): ReaderField => {
            const isSourceColumn = ordinal < CATEGORY_COLUMNS_COUNT;
            const sourceIndex = isSourceColumn ? ordinal : undefined;

            return { field, sourceIndex };
        });
        const groups = this.groupFields().map((field: Field): ReaderField => ({ field, sourceIndex: undefined }));
        const measures = this.columns().flatMap((column: DataViewMetadataColumn): ReaderField[] => {
            const role = getColumnRole(column);
            if (!role || column.index === undefined) {
                return [];
            }
            const sourceIndex = sourceIndexOf(column, sourceColumns, CATEGORY_COLUMNS_COUNT);
            const kind = valueColumnKind(sourceIndex, sourceColumns);
            const field: Field = { id: String(column.index), label: column.displayName, kind, role, movable: true };
            return [{ field, sourceIndex }];
        });
        const readerFields = [...categories, ...groups, ...measures];

        if (!sourceColumns || !this.showsFieldsList()) {
            return readerFields.map((readerField: ReaderField) => readerField.field);
        }
        return fieldsListOf(sourceColumns, readerFields);
    }

    /**
     * The reader keeps the category names beside the columns, so they are shown from there, read only. A pivot table
     * names its row fields; a range only names the category through the parsed category column.
     */
    private categoryFields(): Field[] {
        const dataView = this.dataView();
        const categories = dataView?.categories as DataViewCategoryColumn[] | undefined;
        const categoryNames = dataView?.rowNames?.length ? dataView.rowNames : [categories?.[0]?.source?.displayName];
        return readOnlyFields(CategoricalRoles.Category, categoryNames);
    }

    /** The reader's column fields, read only; only a pivot table has any */
    private groupFields(): Field[] {
        return readOnlyFields(CategoricalRoles.Group, this.dataView()?.columnNames ?? []);
    }

    private dataView(): DataView | undefined {
        return this.getVisual().dataView;
    }

    private columns(): DataViewMetadataColumn[] {
        return this.dataView()?.metadata?.columns ?? [];
    }

    /** No block in PowerPoint; in Excel the block shows the source, or nothing until one is chosen */
    private currentDataSource(): DataSource | null | undefined {
        if (Office.context.host !== Office.HostType.Excel) {
            return undefined;
        }
        return (getOfficeSettings(DATA_SOURCE) as DataSource | undefined) ?? null;
    }

    /**
     * Writes the measure roles the gesture changed. A move out of every bucket (`null`) and a move into Category or
     * Group are not stored, so they are skipped and the refresh puts the field back.
     */
    private applyRoleChanges(changes: RoleChange[]): void {
        const columns = this.columns();
        const returnData = new Map<string, MeasureRoles[]>();

        changes.forEach((change: RoleChange) => {
            const column = columns.find((candidate: DataViewMetadataColumn) => String(candidate.index) === change.fieldId);
            const role = change.role;
            if (!column || column.index === undefined || !isMeasureRole(role)) {
                return;
            }
            this.setNewMeasureRoleSettings(role, column.index, returnData);
            column.roles = { [role]: true };
        });

        this.refreshPane();
        this.notify(returnData, `${this.getClassName()}Observer`);
    }

    /** The pane renders a tick after the menu, so the toolbar's restore lands on an empty body; apply it again once rendered */
    public override setScrollPosition(scrollPosition: number): void {
        super.setScrollPosition(scrollPosition);
        void this.pane?.updateComplete.then(() => super.setScrollPosition(scrollPosition));
    }

    private refreshPane(): void {
        if (this.pane) {
            this.pane.fields = this.buildFields();
        }
    }

    /** Re-reads the data after the dialog closes, since the source may point elsewhere now */
    private editDataSource(): void {
        const dataSource = getOfficeSettings(DATA_SOURCE) as DataSource | undefined;
        if (dataSource) {
            createDialog(dataSource, () => void this.getVisual().getInstance().update(this.getVisual().settings));
        }
    }

    /** Writes the role of the column at `index` and puts the roles array into the observer message */
    private setNewMeasureRoleSettings(role: MeasureRoles, index: number, returnData: Map<string, MeasureRoles[]>): void {
        const settings = this.getVisual().settings;
        if (settings && index < settings.measureRoles.length) {
            settings.measureRoles[index] = role;
            returnData.set("measureRoles", settings.measureRoles);
        }
    }
}

/**
 * Builds the display-only fields of one bucket. Names without a value are dropped, so a source that names no
 * category (a pivot table without row fields) shows an empty Category bucket.
 * @param role the bucket the fields sit in; also prefixes their ids so they never collide with a column index
 * @param names the names the reader provides for that role, in display order
 */
function readOnlyFields(role: CategoricalRoles, names: (string | undefined)[]): Field[] {
    return names
        .filter((name: string | undefined): name is string => !!name)
        .map((name: string): Field => ({ id: `${role}:${name}`, label: name, kind: "text", role, movable: false }));
}

/** A field with no bucket (`null`) or in Category or Group has no measure role to write */
function isMeasureRole(role: FieldRole | null): role is MeasureRoles {
    return role !== null && role in MeasureRoles;
}
